import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    PropertyList,
    PropertyListItem,
    TabOptions,
    TabOptionsContent,
    TabOptionsGroup,
    WeightNumber,
} from '@dndbeyond/character-common-components/es';
import {
    AlignmentContract,
    Background,
    CharacterTraits,
    Constants,
    SizeContract,
    rulesEngineSelectors,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../Shared/actions';
import { PropList, PropListItem } from '../../../Shared/components/legacy/common/PropList';
import { appEnvSelectors } from '../../../Shared/selectors';
import { PaneComponentEnum } from '../../../Shared/utils';
import BackgroundDetail from '../../components/BackgroundDetail';
import ContentGroup from '../../components/ContentGroup';
import TraitContent from '../../components/TraitContent';
import { SheetAppState } from '../../typings';

const DEFAULT_VALUE = '--';

const TAB_KEY = {
    ALL: 'ALL',
    BACKGROUND: 'BACKGROUND',
    CHARACTERISTICS: 'CHARACTERISTICS',
    APPEARANCE: 'APPEARANCE',
};

interface Props extends DispatchProp {
    isVertical: boolean;
    background: Background | null;
    alignment: AlignmentContract | null;
    height: string | null;
    weight: number | null;
    size: SizeContract | null;
    faith: string | null;
    skin: string | null;
    eyes: string | null;
    hair: string | null;
    age: number | null;
    gender: string | null;
    traits: CharacterTraits;
    isReadonly: boolean;
    theme?: any;
}
class Description extends React.PureComponent<Props> {
    static defaultProps = {
        isVertical: false,
    };

    handlePhysicalCharacteristicsClick = (): void => {
        const { dispatch, isReadonly } = this.props;

        if (!isReadonly) {
            dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.DESCRIPTION));
        }
    };

    handleBackgroundClick = (): void => {
        const { dispatch } = this.props;

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.BACKGROUND));
    };

    handleTraitShow = (key: string): void => {
        const { dispatch, isReadonly } = this.props;

        if (!isReadonly) {
            dispatch(
                sidebarActions.paneHistoryStart(PaneComponentEnum.TRAIT, null, {
                    type: key,
                }),
            );
        }
    };

    renderDescriptionItem = (value: number | string | null, fallback: string = DEFAULT_VALUE): React.ReactNode => {
        return value === null ? fallback : value;
    };

    renderCharacteristics = (): React.ReactNode => {
        const { alignment, height, weight, size, faith, skin, eyes, hair, age, gender, isVertical, theme } = this.props;

        //TODO set this up better to better type this
        let PropertyListComponent: React.ComponentType<any>;
        let PropertyListItemComponent: React.ComponentType<any>;
        if (isVertical) {
            PropertyListComponent = PropertyList;
            PropertyListItemComponent = PropertyListItem;
        } else {
            PropertyListComponent = PropList;
            PropertyListItemComponent = PropListItem;
        }

        return (
            <ContentGroup header="Characteristics" theme={theme}>
                <PropertyListComponent
                    className="ct-description__physical"
                    onClick={this.handlePhysicalCharacteristicsClick}
                >
                    <PropertyListItemComponent label="Alignment" percent={25}>
                        {alignment === null ? DEFAULT_VALUE : alignment.name}
                    </PropertyListItemComponent>
                    <PropertyListItemComponent label="Gender" percent={20}>
                        {this.renderDescriptionItem(gender)}
                    </PropertyListItemComponent>
                    <PropertyListItemComponent label="Eyes" percent={20}>
                        {this.renderDescriptionItem(eyes)}
                    </PropertyListItemComponent>
                    <PropertyListItemComponent label="Size" percent={20}>
                        {this.renderDescriptionItem(size ? size.name : null)}
                    </PropertyListItemComponent>
                    <PropertyListItemComponent label="Height" percent={15}>
                        {this.renderDescriptionItem(height)}
                    </PropertyListItemComponent>

                    <PropertyListItemComponent label="Faith" percent={25}>
                        {this.renderDescriptionItem(faith)}
                    </PropertyListItemComponent>
                    <PropertyListItemComponent label="Hair" percent={20}>
                        {this.renderDescriptionItem(hair)}
                    </PropertyListItemComponent>
                    <PropertyListItemComponent label="Skin" percent={20}>
                        {this.renderDescriptionItem(skin)}
                    </PropertyListItemComponent>
                    <PropertyListItemComponent label="Age" percent={20}>
                        {this.renderDescriptionItem(age)}
                    </PropertyListItemComponent>
                    <PropertyListItemComponent label="Weight" percent={15}>
                        {weight === null ? (
                            DEFAULT_VALUE
                        ) : (
                            <WeightNumber number={weight} isDarkMode={theme.isDarkMode} />
                        )}
                    </PropertyListItemComponent>
                </PropertyListComponent>
            </ContentGroup>
        );
    };

    renderTraits = (): React.ReactNode => {
        const { traits, theme } = this.props;

        return (
            <div
                className="ct-description__traits"
                style={theme?.isDarkMode ? { borderColor: `${theme.themeColor}66` } : undefined}
            >
                <TraitContent
                    traits={traits}
                    traitKey={Constants.TraitTypeEnum.PERSONALITY_TRAITS}
                    label="Personality Traits"
                    fallback="+ Add Personality Traits"
                    onClick={this.handleTraitShow}
                />
                <TraitContent
                    traits={traits}
                    traitKey={Constants.TraitTypeEnum.IDEALS}
                    label="Ideals"
                    fallback="+ Add Ideals"
                    onClick={this.handleTraitShow}
                />
                <TraitContent
                    traits={traits}
                    traitKey={Constants.TraitTypeEnum.BONDS}
                    label="Bonds"
                    fallback="+ Add Bonds"
                    onClick={this.handleTraitShow}
                />
                <TraitContent
                    traits={traits}
                    traitKey={Constants.TraitTypeEnum.FLAWS}
                    label="Flaws"
                    fallback="+ Add Flaws"
                    onClick={this.handleTraitShow}
                />
            </div>
        );
    };

    renderAppearance = (): React.ReactNode => {
        const { traits, theme } = this.props;

        if (traits === null) {
            return null;
        }

        return (
            <ContentGroup header="Appearance" theme={theme}>
                <TraitContent
                    traits={traits}
                    traitKey={Constants.TraitTypeEnum.APPEARANCE}
                    fallback="+ Add Appearance information"
                    onClick={this.handleTraitShow}
                />
            </ContentGroup>
        );
    };

    renderBackground = (): React.ReactNode => {
        const { background, theme } = this.props;

        return (
            <ContentGroup header="Background" theme={theme}>
                <BackgroundDetail background={background} onClick={this.handleBackgroundClick} />
            </ContentGroup>
        );
    };

    render() {
        return (
            <div className="ct-description">
                <TabOptions hideBorder={true} initialActiveKey={TAB_KEY.ALL} layoutType="pill">
                    <TabOptionsGroup tabKey={TAB_KEY.ALL} label="All">
                        <TabOptionsContent>
                            {this.renderBackground()}
                            {this.renderCharacteristics()}
                            {this.renderTraits()}
                            {this.renderAppearance()}
                        </TabOptionsContent>
                    </TabOptionsGroup>
                    <TabOptionsGroup tabKey={TAB_KEY.BACKGROUND} label="Background">
                        <TabOptionsContent>{this.renderBackground()}</TabOptionsContent>
                    </TabOptionsGroup>
                    <TabOptionsGroup tabKey={TAB_KEY.CHARACTERISTICS} label="Characteristics">
                        <TabOptionsContent>
                            {this.renderCharacteristics()}
                            {this.renderTraits()}
                        </TabOptionsContent>
                    </TabOptionsGroup>
                    <TabOptionsGroup tabKey={TAB_KEY.APPEARANCE} label="Appearance">
                        <TabOptionsContent>{this.renderAppearance()}</TabOptionsContent>
                    </TabOptionsGroup>
                </TabOptions>
            </div>
        );
    }
}

function mapStateToProps(state: SheetAppState) {
    return {
        background: rulesEngineSelectors.getBackgroundInfo(state),
        alignment: rulesEngineSelectors.getAlignment(state),
        height: rulesEngineSelectors.getHeight(state),
        weight: rulesEngineSelectors.getWeight(state),
        size: rulesEngineSelectors.getSize(state),
        faith: rulesEngineSelectors.getFaith(state),
        skin: rulesEngineSelectors.getSkin(state),
        eyes: rulesEngineSelectors.getEyes(state),
        hair: rulesEngineSelectors.getHair(state),
        age: rulesEngineSelectors.getAge(state),
        gender: rulesEngineSelectors.getGender(state),
        traits: rulesEngineSelectors.getCharacterTraits(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
    };
}

export default connect(mapStateToProps)(Description);
